import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { FeatureAccessService } from './feature-access.service';
import { PermissionGateService } from './permission-gate.service';

/**
 * Route guard for a published HTML document, two layers — the same pair every gated module uses:
 *
 *  1. Tenant enablement — the `tblPage` row for this route must be active in the cached sidebar
 *     payload. A tenant that was never given the document is redirected, because the API would
 *     answer "no such document" anyway.
 *  2. Caller permission — the key named on the route, which must match the `PermissionKey` the
 *     API has configured for that slug. The two are checked independently: this one decides
 *     whether the route opens, the server's decides whether the bytes are served.
 *
 * The wait matters: `has$` default-denies until the permissions slice hydrates, so deciding
 * immediately would bounce legitimate users on every cold navigation. A load failure stays
 * fail-closed — no permissions proven, no page.
 *
 * Denies land on `/dashboard`, the app's landing route.
 */
export const htmlPageGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const featureAccess = inject(FeatureAccessService);
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  const permissionKey = route.data?.['htmlPage']?.permissionKey as string | undefined;
  const pageLink = route.routeConfig?.path ?? '';

  // A route wired without a key would otherwise open to everyone. Unconfigured means closed.
  if (!permissionKey) {
    router.navigateByUrl('dashboard');
    return false;
  }

  if (!featureAccess.isPageActive(pageLink)) {
    router.navigateByUrl('dashboard');
    return false;
  }

  return combineLatest([
    permissionGate.has$(permissionKey),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, me, error]) => me != null || error != null),
    take(1),
    map(([held]) => (held ? true : router.parseUrl('dashboard'))),
  );
};
